"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import styles from "./CallTrace.module.css";

const NODES = ["Edge scheduler", "Published number", "Auto receptionist", "Call queue", "Destination"];
const EVENTS = ["Initiating scheduled call", "Connecting through the PSTN", "Navigating greeting · selecting option 2", "Following queue routing", "Checking the destination"];
const STEP_MS = 1400;
const RESULT_HOLD_MS = 3000;
const motionQuery = "(prefers-reduced-motion: reduce)";
function subscribeMotion(update: () => void) {
  const query = window.matchMedia(motionQuery);
  query.addEventListener("change", update);
  return () => query.removeEventListener("change", update);
}

function TraceIcon({ index, finding }: { index: number; finding: boolean }) {
  return <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {index === 0 ? <><circle cx="16" cy="16" r="10" /><circle cx="16" cy="16" r="4" /><path d="M16 3v5m13 8h-5M16 29v-5M3 16h5m8 0 7-7" /></> : null}
    {index === 1 ? <><path d="M10 5 6 7c-3 7 12 22 19 19l2-4-7-4-3 3-6-6 3-3Z" /><path d="M21 5a7 7 0 0 1 6 6m-6-2a3 3 0 0 1 2 2" /></> : null}
    {index === 2 ? <><rect x="12" y="3" width="8" height="7" rx="2" /><path d="M16 10v7M6 22v-5h20v5" /><rect x="2" y="22" width="8" height="7" rx="2" /><rect x="22" y="22" width="8" height="7" rx="2" /><path d="m13 25 2 2 4-5" /></> : null}
    {index === 3 ? <><rect x="5" y="4" width="22" height="7" rx="2" /><rect x="5" y="14" width="22" height="7" rx="2" /><path d="M9 7.5h1m4 0h8M9 17.5h1m4 0h8M9 27h14" /></> : null}
    {index === 4 && finding ? <><circle cx="8" cy="17" r="5" /><circle cx="24" cy="17" r="5" /><path d="M8 22h16M16 4v5m0 3v.1" /></> : null}
    {index === 4 && !finding ? <><path d="M5 18v-3a11 11 0 0 1 22 0v3m0 5v2c0 3-6 3-9 3" /><rect x="3" y="15" width="6" height="10" rx="3" /><rect x="23" y="15" width="6" height="10" rx="3" /></> : null}
  </svg>;
}

/** A compressed replay of example evidence, not a real-time call or timer. */
export function CallTrace({ route, finding }: { route: string[]; finding: boolean }) {
  const reduced = useSyncExternalStore(subscribeMotion, () => window.matchMedia(motionQuery).matches, () => true);
  const root = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);
  const shownStep = reduced ? NODES.length : step;
  const complete = shownStep >= NODES.length;
  const active = !reduced && !paused && visible && tabVisible;
  const running = active && !complete;

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)), { threshold: 0.25 });
    observer.observe(el);
    const updateTab = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", updateTab);
    updateTab();
    return () => { observer.disconnect(); document.removeEventListener("visibilitychange", updateTab); };
  }, []);

  useEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => setStep(current => current >= NODES.length ? 0 : current + 1), complete ? RESULT_HOLD_MS : STEP_MS);
    return () => window.clearTimeout(timer);
  }, [active, complete, step]);

  const state = paused ? "paused" : complete ? "complete" : running ? "playing" : "waiting";
  return <div ref={root} className={styles.trace} data-state={state} data-step={shownStep}>
    <div className={styles.topline}>
      <span className={styles.signature}><span aria-hidden="true">⌁</span> EDGE / CALL TRACE</span>
      <div className={styles.playback}><span>Illustrative replay · compressed timing</span>
        {reduced ? <span className={styles.motionOff}>Motion off</span> : <button type="button" onClick={() => setPaused(value => !value)} aria-label={paused ? "Resume call trace" : "Pause call trace"}>
          <span aria-hidden="true">{paused ? "▶" : "Ⅱ"}</span> {paused ? "Resume" : "Pause"}
        </button>}
      </div>
    </div>
    <ol className={styles.nodes} aria-label="Illustrative call path">
      {NODES.map((node, index) => {
        const reached = shownStep >= index;
        const fault = finding && index >= 3 && reached;
        const passed = shownStep > index && !fault;
        return <li key={node} data-verdict={fault ? "fail" : passed ? "pass" : "pending"} className={`${styles.node} ${passed ? styles.passed : ""} ${fault ? styles.fault : ""} ${shownStep === index ? styles.current : ""}`}>
          <div className={styles.nodeTop}>
            <span className={styles.icon}><TraceIcon index={index} finding={finding} /><span className={styles.nodeNumber}>{fault ? "!" : passed ? "✓" : String(index + 1).padStart(2, "0")}</span></span>
            {index < NODES.length - 1 ? <span className={`${styles.connector} ${shownStep > index ? styles.connected : ""}`} aria-hidden="true"><span className={styles.packet} style={{ animationPlayState: running ? "running" : "paused" }} /></span> : null}
          </div>
          <div className={styles.nodeCopy}><span className={styles.nodeLabel}>{node}</span><span className={styles.observation}>{route[index]}</span>{fault ? <span className={styles.exception}>{index === 3 ? "Routing exception" : "Unexpected destination"}</span> : null}</div>
        </li>;
      })}
    </ol>
    <div className={styles.event}>
      <span className={styles.wave} aria-hidden="true">{[5,12,19,9,24,15,7,18,11].map((height,index)=><i key={index} style={{height,animationDelay:`${index * -110}ms`,animationPlayState:running ? "running" : "paused"}} />)}</span>
      <p>{complete ? (finding ? "Exception traced to the after-hours overflow rule." : "Support agent reached. Routing checks passed.") : paused ? "Replay paused. Resume to follow the call." : EVENTS[shownStep]}</p>
      <span className={styles.progress}>{complete ? "TRACE COMPLETE" : `${String(shownStep + 1).padStart(2, "0")} / 05`}</span>
    </div>
    <span className="sr-only">{finding ? "Illustrative looping call trace: after-hours routing exception identified." : "Illustrative looping call trace: all routing checks passed."}</span>
  </div>;
}
